import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPatch, apiPost, ApiError } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { ToolGate } from '../../components/ToolGate';
import { Button, Field, Input, Alert, Loading, EmptyState, Badge, Spinner } from '../../components/ui';
import { tierColor, relTime } from '../../lib/types';
import { ResearchButton } from '../../components/DeepResearch';
import { UsageMeter, remainingShort } from '../../lib/usage';

const TARGET_OPTIONS = [50, 150, 300] as const;

interface Industry { name: string; notes?: string }
interface Profile {
  id: string; name: string; isDefault: boolean; sellTo: string | null;
  geoScope: string | null; geoCenter: string | null; geoRadiusMi: number | null;
  targetIndustries: Industry[]; idealCustomerDesc: string | null;
}
interface Ctx { group: { name: string | null; industry: string | null }; profiles: Profile[]; defaultProfileId: string | null }
interface Found { id: number; businessName: string; address?: string; phone?: string }

interface Batch { id: number; name: string; source: string; createdAt: string; total: number; scored: number; unscored: number }

export default function LeadFinder() {
  const ctx = useAsync<Ctx>(() => apiGet('/api/tools/leadfinder/context'), []);
  const usage = useAsync<{ meters: { lead_source: UsageMeter } }>(() => apiGet('/api/usage'), []);
  const batchesQ = useAsync<{ batches: Batch[] }>(() => apiGet('/api/batches'), []);
  const [sessionBatchId, setSessionBatchId] = useState<number | null>(null);
  const [profileId, setProfileId] = useState<string>('');
  const [industry, setIndustry] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newInd, setNewInd] = useState('');
  const [target, setTarget] = useState<number>(150);
  // Search radius, shown inline so it's adjustable without leaving the page. It
  // PERSISTS to the account's saved targeting default (one source of truth — /auto
  // reads geo_radius_mi from the profile server-side), not a per-search override.
  const [radius, setRadius] = useState<number | ''>('');
  const savedRadius = useRef<number | null>(null);
  const [auto, setAuto] = useState<{ busy: boolean; res: any; err: string | null; limited?: boolean }>({ busy: false, res: null, err: null });
  const [manualOpen, setManualOpen] = useState(false);
  const [contacts, setContacts] = useState<Found[]>([]);
  const [scores, setScores] = useState<Record<number, { score: number; tier: string }>>({});
  const [scoreMsg, setScoreMsg] = useState<string | null>(null);
  const [scoring, setScoring] = useState(false);
  const [scoreProg, setScoreProg] = useState<{ done: number; total: number } | null>(null);
  const navigate = useNavigate();

  const profiles = ctx.data?.profiles || [];
  const leadUsage = usage.data?.meters?.lead_source || null;
  const leadRemaining = leadUsage ? leadUsage.remaining : null;
  const active = profiles.find((p) => p.id === (profileId || ctx.data?.defaultProfileId)) || profiles[0];
  const industryList = active?.targetIndustries || [];
  const industries = industryList.map((i) => i.name);
  // Effective focus industry: explicit pick, else the first in the list (scope,
  // don't blend), else empty (profile has none — search runs off ICP/geo).
  const selected = industry ?? industries[0] ?? '';

  // Seed the radius box from the account's saved default whenever the active
  // profile changes (25mi fallback — the same effective default /auto uses).
  useEffect(() => {
    if (!active) return;
    const r = active.geoRadiusMi ?? 25;
    setRadius(r);
    savedRadius.current = r;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  // Persist the radius to the saved targeting profile (Settings default). Called on
  // blur and again just before a search, so the run always uses the shown value.
  async function persistRadius() {
    if (!active || typeof radius !== 'number' || radius === savedRadius.current) return;
    try {
      await apiPatch(`/api/settings/profiles/${active.id}`, { geoRadiusMi: radius });
      savedRadius.current = radius;
    } catch { /* non-fatal — search still runs with the last saved radius */ }
  }

  async function runAuto(focus: string) {
    await persistRadius();
    setAuto({ busy: true, res: null, err: null });
    try {
      const res = await apiPost<any>('/api/tools/leadfinder/auto', { profileId: active?.id, industry: focus || undefined, run: true, target });
      setAuto({ busy: false, res, err: null });
      if (res.contacts?.length) setContacts((c) => [...res.contacts, ...c]);
      setSessionBatchId(res.batchId || null);
      usage.reload(); batchesQ.reload();
    } catch (e) {
      const limited = e instanceof ApiError && e.status === 402;
      setAuto({ busy: false, res: null, err: e instanceof ApiError ? e.message : 'Search failed.', limited });
      if (limited) usage.reload();
    }
  }

  // Multi-industry accounts: one click across ALL their industries in sequence
  // (both re-validation agents flagged the missing "run all" for broad ICPs). The
  // per-industry target still applies, and the monthly cap stops it early if hit.
  async function runAllIndustries() {
    if (!industries.length) return runAuto('');
    await persistRadius();
    setAuto({ busy: true, res: null, err: null });
    let totalInserted = 0;
    const done: string[] = [];
    try {
      for (const ind of industries) {
        const res = await apiPost<any>('/api/tools/leadfinder/auto', { profileId: active?.id, industry: ind, run: true, target });
        if (res.contacts?.length) setContacts((c) => [...res.contacts, ...c]);
        totalInserted += res.inserted || 0;
        done.push(ind);
        setAuto({ busy: true, res: { placesSearchable: true, ran: true, inserted: totalInserted, _progress: `${done.length}/${industries.length}` }, err: null });
        if (res.capped) break; // monthly budget reached — stop the loop
      }
      setAuto({ busy: false, res: { placesSearchable: true, ran: true, inserted: totalInserted, _all: done }, err: null });
      setSessionBatchId(null); // multiple batches (one per industry) — browse them below
      usage.reload(); batchesQ.reload();
    } catch (e) {
      const limited = e instanceof ApiError && e.status === 402;
      setAuto({ busy: false, res: null, err: e instanceof ApiError ? e.message : 'Search failed.', limited });
      if (limited) usage.reload();
    }
  }

  // Add a brand-new industry inline: save it back to the profile (Settings' one
  // list — not a second copy), select it, and search immediately.
  async function addIndustryAndSearch() {
    const val = newInd.trim();
    if (!val || !active) return;
    setAdding(false);
    setNewInd('');
    setIndustry(val);
    if (!industries.some((i) => i.toLowerCase() === val.toLowerCase())) {
      try { await apiPatch(`/api/settings/profiles/${active.id}`, { targetIndustries: [...industryList, { name: val, notes: '' }] }); await ctx.reload(); } catch { /* still search */ }
    }
    runAuto(val);
  }

  function onSelectIndustry(v: string) {
    if (v === '__add__') { setAdding(true); return; }
    setAdding(false);
    setIndustry(v);
  }

  // Land in Contacts filtered to exactly the batch we just sourced, best-scored
  // first — one continuous flow (Phase 10). Disabled while scoring so it can never
  // interrupt an in-flight run.
  // Hand off into Contacts — to the batch view when this was a single search run,
  // else to the full list (multi-industry runs made several batches; browse below).
  function goToContacts() {
    if (sessionBatchId) navigate(`/contacts?batch=${sessionBatchId}`);
    else navigate('/contacts?sort=score');
  }
  function openInContacts() {
    if (scoring || !contacts.length) return;
    goToContacts();
  }

  // Score the batch we just sourced (by id, so it scores THESE leads, not older
  // group-wide unscored ones). Explicit batch size keeps cost the user's choice
  // (Phase 9). Chunked with a real X-of-Y progress state; on completion it hands
  // off straight into Contacts. An early click on the completion action can't
  // cancel this — that action is disabled until `scoring` clears (Phase 10 bug).
  async function scoreBatch(mode: 20 | 50 | 'all') {
    if (scoring) return;
    const pending = contacts.filter((c) => !scores[c.id]).map((c) => c.id);
    const ids = mode === 'all' ? pending : pending.slice(0, mode);
    if (!ids.length) { setScoreMsg('These are already scored — opening your contacts.'); openInContacts(); return; }
    setScoring(true); setScoreMsg(null); setScoreProg({ done: 0, total: ids.length });
    const m = { ...scores };
    let done = 0;
    try {
      for (let i = 0; i < ids.length; i += 25) {
        const chunk = ids.slice(i, i + 25);
        const r = await apiPost<{ results: { id: number; score: number; tier: string }[] }>('/api/tools/leadfinder/score', { contactIds: chunk });
        (r.results || []).forEach((x) => { if (x.tier) m[x.id] = { score: x.score, tier: x.tier }; });
        done += chunk.length;
        setScores({ ...m });
        setScoreProg({ done, total: ids.length });
      }
      setScoreProg(null);
      setScoring(false);
      // Continuous handoff: drop them into Contacts, filtered to this batch.
      goToContacts();
    } catch (e) {
      setScoreProg(null);
      setScoreMsg(e instanceof ApiError ? e.message : 'Scoring failed.');
      setScoring(false);
    }
  }

  return (
    <ToolGate tool="lead_finder" title="Lead Score">
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="h-display text-3xl text-soft">Lead Score</h1>
            <p className="mt-1 text-sm text-muted">Find your prospects and score them by fit — built from your targeting.</p>
          </div>
          <Link to="/contacts?import=1" className="shrink-0 text-sm font-semibold text-electric hover:underline">Import CSV →</Link>
        </div>

        {ctx.loading ? (
          <Loading label="Loading your targeting…" />
        ) : ctx.error ? (
          <Alert>{ctx.error}</Alert>
        ) : !active ? (
          <EmptyState title="No targeting profile yet" body="Finish onboarding or add a targeting profile in Settings, and Lead Score will search for you automatically." />
        ) : (
          <div className="card p-5">
            {profiles.length > 1 && (
              <div className="mb-4">
                <Field label="Targeting profile">
                  <select className="input" value={active.id} onChange={(e) => { setProfileId(e.target.value); setAuto({ busy: false, res: null, err: null }); }}>
                    {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}{p.isDefault ? ' (default)' : ''}</option>)}
                  </select>
                </Field>
              </div>
            )}
            <div className="text-sm text-muted">You're looking for</div>
            <div className="mt-1 font-head text-lg text-soft">
              {active.idealCustomerDesc || industries.join(', ') || ctx.data?.group.industry || 'your prospects'}
            </div>
            <div className="mt-1 text-sm text-muted">{active.geoCenter || active.geoScope || 'anywhere'}</div>

            {/* Industry dropdown — driven by the same Settings list (one source). */}
            <div className="mt-4 max-w-xs">
              <Field label="Industry to search">
                {adding ? (
                  <div className="flex gap-2">
                    <Input autoFocus value={newInd} onChange={(e) => setNewInd(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addIndustryAndSearch(); } }}
                      placeholder="Type a new industry…" />
                    <Button onClick={addIndustryAndSearch} loading={auto.busy} className="shrink-0">Add &amp; search</Button>
                    <Button variant="ghost" onClick={() => { setAdding(false); setNewInd(''); }} className="shrink-0">✕</Button>
                  </div>
                ) : (
                  <select className="input" value={selected} onChange={(e) => onSelectIndustry(e.target.value)}>
                    {industries.length === 0 && <option value="" disabled>No industries yet</option>}
                    {industries.map((n) => <option key={n} value={n}>{n}</option>)}
                    <option value="__add__">+ Add new industry…</option>
                  </select>
                )}
              </Field>
            </div>

            {/* Target picker — how many prospects to add. Prevents one click from
                flooding the pipeline or burning the monthly allowance (Phase 9). */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted">Find up to</span>
              {TARGET_OPTIONS.map((n) => (
                <button key={n} onClick={() => setTarget(n)}
                  className={`rounded-lg border px-2.5 py-1 text-sm font-semibold transition ${target === n ? 'border-electric bg-electric/10 text-electric' : 'border-line text-muted hover:text-soft'}`}>
                  {n}
                </button>
              ))}
              <span className="text-sm text-muted">prospects</span>
              <span className="mx-1 h-4 w-px bg-line" />
              <span className="text-sm text-muted">within</span>
              <input
                type="number" min={1} max={500}
                className="input w-16 py-1 text-center text-sm"
                value={radius}
                onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setRadius(v === '' ? '' : Number(v)); }}
                onBlur={persistRadius}
                aria-label="Search radius in miles"
                title="Distance to search around each location — saved to your targeting default"
              />
              <span className="text-sm text-muted">mi</span>
              {leadRemaining != null && (
                <span className="ml-auto text-xs text-muted">{leadUsage ? remainingShort(leadUsage) : ''} · <Link to="/usage" className="font-semibold text-electric hover:underline">Usage</Link></span>
              )}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <Button onClick={() => runAuto(selected)} loading={auto.busy} disabled={leadRemaining === 0}>{selected ? `Find ${selected} prospects` : 'Find my prospects'}</Button>
              {industries.length > 1 && (
                <Button variant="ghost" onClick={runAllIndustries} loading={auto.busy} disabled={leadRemaining === 0}>Find across all {industries.length} industries</Button>
              )}
              <Button variant="ghost" onClick={() => setManualOpen((v) => !v)}>{manualOpen ? 'Hide manual search' : 'Search something specific'}</Button>
            </div>
            {leadRemaining === 0 && (
              <div className="mt-3"><Alert kind="info">You've used all {leadUsage?.included} Lead Score contacts this cycle. <Link to="/usage" className="font-semibold underline">Buy a top-up ({leadUsage?.topup.label}) →</Link></Alert></div>
            )}
            {auto.err && <div className="mt-3"><Alert kind={auto.limited ? 'info' : 'error'}>{auto.err}{auto.limited && <> <Link to="/usage" className="font-semibold underline">Manage usage →</Link></>}</Alert></div>}
            {auto.res && <AutoResult res={auto.res} />}
          </div>
        )}

        {manualOpen && <ManualSearch onFound={(c, batchId) => { setContacts((prev) => [...c, ...prev]); setSessionBatchId(batchId ?? null); usage.reload(); batchesQ.reload(); }} termHint={industries[0] || active?.idealCustomerDesc?.split(' ').slice(0, 3).join(' ')} locHint={active?.geoCenter || undefined} />}

        {contacts.length > 0 && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-baseline gap-3">
                <h2 className="font-head text-lg font-semibold text-soft">New leads ({contacts.length})</h2>
                <button
                  onClick={openInContacts}
                  disabled={scoring}
                  className="text-sm font-semibold text-electric hover:underline disabled:cursor-not-allowed disabled:text-muted disabled:no-underline"
                  title={scoring ? 'Finishing scoring…' : 'Open these leads in Contacts'}
                >
                  Open in Contacts →
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted">Score &amp; open:</span>
                <Button variant="ghost" onClick={() => scoreBatch(20)} disabled={scoring} className="px-2.5 py-1 text-sm">Next 20</Button>
                <Button variant="ghost" onClick={() => scoreBatch(50)} disabled={scoring} className="px-2.5 py-1 text-sm">Next 50</Button>
                <Button variant="ghost" onClick={() => scoreBatch('all')} disabled={scoring} className="px-2.5 py-1 text-sm">All {contacts.length}</Button>
              </div>
            </div>
            {scoreProg && (
              <div className="rounded-xl border border-electric/40 bg-electric/5 px-3.5 py-3">
                <div className="flex items-center justify-between text-sm font-semibold text-electric">
                  <span className="flex items-center gap-2"><Spinner className="h-4 w-4" /> Scoring your leads…</span>
                  <span>{scoreProg.done} of {scoreProg.total}</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-3">
                  <div className="h-full rounded-full bg-electric transition-all" style={{ width: `${Math.round((scoreProg.done / Math.max(1, scoreProg.total)) * 100)}%` }} />
                </div>
                <p className="mt-1.5 text-xs text-muted">Hang tight — we'll drop you into your contacts when this finishes.</p>
              </div>
            )}
            {scoreMsg && !scoreProg && <Alert kind="info">{scoreMsg}</Alert>}
            <div className="card divide-y divide-line">
              {contacts.map((c) => (
                <div key={c.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-head font-semibold text-soft">{c.businessName}</div>
                    <div className="truncate text-xs text-muted">{c.address || c.phone || '—'}</div>
                  </div>
                  <ResearchButton contactId={c.id} />
                  {scores[c.id] && <Badge className={`border-line ${tierColor(scores[c.id].tier)}`}>{scores[c.id].tier} · {scores[c.id].score}</Badge>}
                </div>
              ))}
            </div>
          </div>
        )}

        <BatchBrowser batches={batchesQ.data?.batches || []} loading={batchesQ.loading} />
      </div>
    </ToolGate>
  );
}

// Browsable list of every search run + CSV import as its own batch, with a
// scored/unscored count. Clicking one opens Contacts filtered to that batch, where
// the full scoring controls live (Phase 11).
function BatchBrowser({ batches, loading }: { batches: Batch[]; loading: boolean }) {
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  if (loading && !batches.length) return null;
  if (!batches.length) return null;
  const sorted = [...batches].sort((a, b) => sort === 'newest'
    ? (a.createdAt < b.createdAt ? 1 : -1)
    : (a.createdAt > b.createdAt ? 1 : -1));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-head text-lg font-semibold text-soft">Your batches ({batches.length})</h2>
        <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
          Sort
          <select className="input py-1 text-sm" value={sort} onChange={(e) => setSort(e.target.value as 'newest' | 'oldest')}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </label>
      </div>
      <div className="card divide-y divide-line">
        {sorted.map((b) => (
          <Link key={b.id} to={`/contacts?batch=${b.id}`} className="flex items-center gap-3 px-4 py-3 transition hover:bg-surface-3">
            <div className="min-w-0 flex-1">
              <div className="truncate font-head font-semibold text-soft">{b.name}</div>
              <div className="text-xs text-muted">{b.source === 'import' ? 'Imported list' : 'Search'} · {relTime(b.createdAt)}</div>
            </div>
            {b.unscored > 0
              ? <Badge className="border-victory/40 text-victory">{b.scored}/{b.total} scored</Badge>
              : <Badge className="border-success/40 text-success">All {b.total} scored</Badge>}
            <span className="text-electric">→</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function AutoResult({ res }: { res: any }) {
  if (res.placesSearchable === false) {
    // Honest fallback → make the next step obvious instead of a dead end.
    return (
      <div className="mt-3 space-y-2">
        <Alert kind="info">{res.message}</Alert>
        <div className="flex flex-wrap gap-2">
          <Link to="/contacts?import=1" className="btn-primary text-sm">Import a list (CSV)</Link>
          <Link to="/contacts" className="btn-ghost text-sm">Add a company by hand</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-2">
      {res._progress && <div className="flex items-center gap-2 text-sm text-muted"><Spinner className="h-4 w-4" /> Searching your industries… ({res._progress})</div>}
      {res._all && <Alert kind="success">Searched all {res._all.length} industries — added {res.inserted} new lead{res.inserted === 1 ? '' : 's'}.</Alert>}
      {!res._all && !res._progress && res.queries?.length > 0 && (
        <div className="text-xs text-muted">Searched: {res.queries.map((q: any) => [q.searchTerm, q.location].filter(Boolean).join(', ')).join(' · ')}</div>
      )}
      {res.ran === false && res.message && <Alert kind="info">{res.message}</Alert>}
      {res.ran && !res._all && !res._progress && <Alert kind="success">Added {res.inserted} new lead{res.inserted === 1 ? '' : 's'}{res.skipped ? `, skipped ${res.skipped} you already had` : ''}.</Alert>}
      {res.ran && res.capped && <Alert kind="info">Reached your target / monthly limit — stopped after {res.inserted}. <Link to="/usage" className="font-semibold underline">Usage →</Link></Alert>}
    </div>
  );
}

function ManualSearch({ onFound, termHint, locHint }: { onFound: (c: Found[], batchId?: number | null) => void; termHint?: string; locHint?: string }) {
  const [f, setF] = useState({ searchTerm: '', location: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function run(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await apiPost<{ inserted: number; skipped: number; contacts: Found[]; batchId: number | null }>('/api/tools/leadfinder/source', { ...f, maxResults: 20 });
      setMsg(`Added ${r.inserted}, skipped ${r.skipped} duplicates.`);
      if (r.contacts?.length) onFound(r.contacts, r.batchId);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Search failed.');
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={run} className="card p-5">
      <h2 className="font-head font-semibold text-soft">Manual search</h2>
      <p className="mt-0.5 text-sm text-muted">Search a specific business type and place — overrides the automatic search.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <Field label="Business type"><Input value={f.searchTerm} onChange={(e) => setF({ ...f, searchTerm: e.target.value })} placeholder={termHint || 'e.g. dental practices'} /></Field>
        <Field label="Location"><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder={locHint || 'e.g. Boston, MA'} /></Field>
        <Button type="submit" loading={busy}>Search Maps</Button>
      </div>
      {busy && <div className="mt-3 flex items-center gap-2 text-sm text-muted"><Spinner className="h-4 w-4" /> Searching…</div>}
      {msg && <div className="mt-3"><Alert kind="success">{msg}</Alert></div>}
      {err && <div className="mt-3"><Alert>{err}</Alert></div>}
    </form>
  );
}
